// pages/Evaluation.jsx
import { useState, useEffect } from "react";
import { getAllLessons } from "../../api/lesson";
import {
  getStatementsByLesson,
  createStatement,
  updateStatement,
  deleteStatement,
} from "../../api/evaluationApi";
import Loading from "../loading";
import GetAdmin from "../../utils/getAdmin";
import NotFoundPage from "./404";
import Header from "../Header";
import Navigation from "../Navigation";
import Notification from "../Notification";
import AddEvaluationForm from "../Forms/addEvaluationStatment";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";

const Evaluation = () => {
  const [loading, setLoading] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [allLessons, setAllLessons] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedBook, setSelectedBook] = useState("");
  const [filteredLessons, setFilteredLessons] = useState([]);
  const [statementsMap, setStatementsMap] = useState({});
  const [addStatement, setAddStatement] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationProps, setNotificationProps] = useState({});
  const [refresh, setRefresh] = useState(false);
  const [expandedLessons, setExpandedLessons] = useState({});

  useEffect(() => {
    const fetchAdmin = async () => {
      const admin = await GetAdmin();
      setAdminData(admin);
    };
    fetchAdmin();
  }, []);

  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const res = await getAllLessons();
        setAllLessons(res.data.lessons || []);
      } catch (err) {
        console.error("Error fetching lessons:", err);
      }
    };
    fetchLessons();
  }, []);

  useEffect(() => {
    if (selectedGrade && selectedBook) {
      const filtered = allLessons
        .filter(
          (lesson) =>
            lesson.grade === selectedGrade && lesson.book === selectedBook,
        )
        .sort((a, b) => {
          if (a.season !== b.season) {
            return a.season - b.season;
          }
          return a.lesson - b.lesson;
        });
      setFilteredLessons(filtered);
    } else {
      setFilteredLessons([]);
    }
  }, [selectedGrade, selectedBook, allLessons]);

  useEffect(() => {
    const fetchStatementsForLessons = async () => {
      if (filteredLessons.length === 0) return;
      setLoading(true);
      try {
        const newMap = {};
        for (const lesson of filteredLessons) {
          const res = await getStatementsByLesson(lesson._id);
          newMap[lesson._id] = res.statements || [];
        }
        setStatementsMap(newMap);
      } catch (err) {
        console.error("Error fetching statements:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStatementsForLessons();
  }, [filteredLessons, refresh]);

  const toggleLessonExpand = (lessonId) => {
    setExpandedLessons((prev) => ({
      ...prev,
      [lessonId]: !prev[lessonId],
    }));
  };

  const handleDeleteStatement = async (id, lessonId) => {
    if (!window.confirm("آیا از حذف این گزاره مطمئن هستید؟")) return;
    try {
      const res = await deleteStatement(id);
      if (res.message && res.message.includes("حذف")) {
        setStatementsMap((prev) => ({
          ...prev,
          [lessonId]: (prev[lessonId] || []).filter((s) => s._id !== id),
        }));
        showSuccess("گزاره با موفقیت حذف شد");
      } else {
        showError(res.message || "خطا در حذف گزاره");
      }
    } catch (err) {
      showError("خطا در ارتباط با سرور");
    }
  };

  const showSuccess = (msg) => {
    setNotificationProps({ message: msg, type: "success", duration: 3000 });
    setShowNotification(true);
  };

  const showError = (msg) => {
    setNotificationProps({ message: msg, type: "error", duration: 4000 });
    setShowNotification(true);
  };

  if (!adminData) return <Loading />;

  if (adminData.role !== "manager" && adminData.role !== "questionMaker") {
    return <NotFoundPage />;
  }

  return (
    <div className="w-full flex justify-center flex-col items-center">
      {showNotification && (
        <Notification
          {...notificationProps}
          setShowNotification={setShowNotification}
          showNotification={showNotification}
        />
      )}

      <Header adminName={adminData.name} />

      <div className="flex flex-col md:flex-row gap-6 justify-between w-full px-4">
        <div className="w-full md:w-1/4 mb-6 lg:mb-0">
          <Navigation />
        </div>

        <div className="w-full md:w-3/4">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              مدیریت گزاره‌های ارزشیابی توصیفی
            </h2>

            {/* فیلتر پایه و کتاب */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  پایه تحصیلی
                </label>
                <select
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">همه پایه‌ها</option>
                  <option value="1">اول</option>
                  <option value="2">دوم</option>
                  <option value="3">سوم</option>
                  <option value="4">چهارم</option>
                  <option value="5">پنجم</option>
                  <option value="6">ششم</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  کتاب
                </label>
                <select
                  value={selectedBook}
                  onChange={(e) => setSelectedBook(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">همه کتاب‌ها</option>
                  <option value="1">ریاضی</option>
                  <option value="2">فارسی و نگارش</option>
                  <option value="3">علوم</option>
                  <option value="4">قرآن</option>
                  <option value="5">املا</option>
                  <option value="6">هدیه‌های آسمانی</option>
                  <option value="7">مطالعات اجتماعی</option>
                </select>
              </div>
            </div>

            {/* نمایش دروس و گزاره‌ها */}
            {loading ? (
              <Loading />
            ) : filteredLessons.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                {selectedGrade && selectedBook
                  ? "هیچ درسی برای این پایه و کتاب یافت نشد"
                  : "لطفاً پایه و کتاب را انتخاب کنید"}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredLessons.map((lesson) => {
                  const statements = statementsMap[lesson._id] || [];
                  const isExpanded = expandedLessons[lesson._id] || false;

                  return (
                    <div
                      key={lesson._id}
                      className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
                    >
                      {/* هدر درس (قابل کلیک برای باز/بسته کردن) */}
                      <div
                        onClick={() => toggleLessonExpand(lesson._id)}
                        className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <div>
                          <h3 className="text-base font-semibold text-gray-800">
                            {`${lesson.title} (فصل ${lesson.season} - درس ${lesson.lesson})`}
                          </h3>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-blue-600">
                            {statements.length} گزاره
                          </span>
                          {isExpanded ? (
                            <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                      </div>

                      {/* محتوای باز شده */}
                      {isExpanded && (
                        <div className="p-4 pt-0 border-t border-gray-100">
                          {/* لیست گزاره‌ها */}
                          {statements.length > 0 && (
                            <div className="space-y-2 mb-4">
                              {statements.map((stmt) => (
                                <div
                                  key={stmt._id}
                                  className="bg-gray-50 rounded-lg p-3 border border-gray-200 flex justify-between items-start"
                                >
                                  <p className="text-sm text-gray-700 flex-1">
                                    {stmt.content}
                                  </p>
                                  <div className="flex gap-2 mr-2">
                                    <button
                                      onClick={() =>
                                        setAddStatement({
                                          type: "edit",
                                          id: stmt._id,
                                          content: stmt.content,
                                          lessonId: stmt.lessonId,
                                        })
                                      }
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                    >
                                      ویرایش
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDeleteStatement(
                                          stmt._id,
                                          lesson._id,
                                        )
                                      }
                                      className="text-red-600 hover:text-red-800 text-sm"
                                    >
                                      حذف
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* فرم افزودن/ویرایش */}
                          {addStatement &&
                          addStatement.lessonId === lesson._id ? (
                            <AddEvaluationForm
                              lessonId={lesson._id}
                              grade={selectedGrade}
                              book={selectedBook}
                              onCancel={() => setAddStatement(null)}
                              setStatementsMap={setStatementsMap}
                              showSuccess={showSuccess}
                              showError={showError}
                              addStatement={addStatement}
                            />
                          ) : (
                            <button
                              onClick={() =>
                                setAddStatement({
                                  lessonId: lesson._id,
                                })
                              }
                              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                              <span className="text-lg">+</span> افزودن گزاره
                              جدید
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Evaluation;
